import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAltOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import ClearIcon from '@mui/icons-material/FilterAltOffOutlined';
import { PageHeader } from '../../components/PageHeader';
import { useAuth } from '../../context/authContext';
import { downloadBlob } from '../../utils/downloadBlob';
import { KpiCard, KpiGrid } from '../dashboard/KpiCard';
import {
  CompletedConsultationFilters,
  CompletedConsultationRow,
  completePersonalAppointment,
  exportCompletedConsultations,
  getCompletedConsultationFilterOptions,
  getCompletedConsultationSummary,
  getPersonalAppointmentHistory,
  listCompletedConsultations,
  reopenPersonalAppointment,
} from '../../api/personalAppointments';
import { PersonalAppointmentStatus } from '../../types/lead';
import { leadDetailState } from '../../utils/leadNavigation';

const STATUS_LABEL: Record<PersonalAppointmentStatus, string> = {
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};
const STATUS_COLOR: Record<PersonalAppointmentStatus, 'default' | 'success' | 'error'> = {
  SCHEDULED: 'default',
  COMPLETED: 'success',
  CANCELLED: 'error',
};

const date = (v: string | null) =>
  v
    ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
const time = (v: string | null) =>
  v ? new Date(v).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—';
const dateTime = (v: string | null) => (v ? `${date(v)}, ${time(v)}` : '—');
const errorText = (err: unknown, fallback: string) =>
  isAxiosError(err)
    ? ((err.response?.data as { message?: string } | undefined)?.message ?? fallback)
    : fallback;

const EMPTY: CompletedConsultationFilters = {};

/** A person (counselor) with their login underneath. */
const PersonCell = ({ name, login }: { name: string; login: string }) => (
  <Box sx={{ lineHeight: 1.25, py: 0.5, minWidth: 0 }}>
    <Typography variant="body2" noWrap>
      {name}
    </Typography>
    <Typography variant="caption" color="text.secondary" noWrap component="div">
      {login}
    </Typography>
  </Box>
);

/** Completed Consultation (2026-11-05) — every Personal Appointment marked
 * completed, read from the appointments themselves: who it was with, the
 * counselor it was assigned to and their login, and who completed it.
 * Search and filters combine; the summary and the Excel export follow the
 * same filters. A mistaken completion can be reopened (with a reason) —
 * it leaves this list, and its history keeps the completion. */
export const CompletedConsultationsPage = () => {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canChangeStatus = hasPermission('personal_appointments', 'complete');

  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<CompletedConsultationFilters>(EMPTY);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [reopenTarget, setReopenTarget] = useState<CompletedConsultationRow | null>(null);
  const [reason, setReason] = useState('');
  const [historyTarget, setHistoryTarget] = useState<CompletedConsultationRow | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(
    null,
  );
  const [exporting, setExporting] = useState(false);

  // Search as you type, without a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput.trim() || undefined }));
      setPaginationModel((m) => ({ ...m, page: 0 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const setFilter = <K extends keyof CompletedConsultationFilters>(
    key: K,
    value: CompletedConsultationFilters[K] | '',
  ) => {
    setFilters((f) => ({ ...f, [key]: value === '' ? undefined : value }));
    setPaginationModel((m) => ({ ...m, page: 0 }));
  };
  const clearFilters = () => {
    setSearchInput('');
    setFilters(EMPTY);
    setPaginationModel((m) => ({ ...m, page: 0 }));
  };
  const hasFilters = Object.values(filters).some((v) => v !== undefined) || searchInput !== '';

  const options = useQuery({
    queryKey: ['completed-consultations', 'filters'],
    queryFn: getCompletedConsultationFilterOptions,
  });
  const list = useQuery({
    queryKey: ['completed-consultations', 'list', filters, paginationModel],
    queryFn: () =>
      listCompletedConsultations({
        ...filters,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['completed-consultations', 'summary', filters],
    queryFn: () => getCompletedConsultationSummary(filters),
    placeholderData: keepPreviousData,
  });
  const history = useQuery({
    queryKey: ['completed-consultations', 'history', historyTarget?.id],
    queryFn: () => getPersonalAppointmentHistory(historyTarget!.id),
    enabled: !!historyTarget,
  });

  // A status change shows here at once — and wherever else appointments appear.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['completed-consultations'] });
    void queryClient.invalidateQueries({ queryKey: ['personal-appointments'] });
  };
  const reopen = useMutation({
    mutationFn: () => reopenPersonalAppointment(reopenTarget!.id, reason.trim()),
    onSuccess: () => {
      setNotice({
        severity: 'success',
        text: `${reopenTarget!.appointmentId} reopened — back to Scheduled. The completion stays in its history.`,
      });
      setReopenTarget(null);
      setReason('');
      refresh();
    },
  });
  const complete = useMutation({
    mutationFn: (row: CompletedConsultationRow) => completePersonalAppointment(row.id),
    onSuccess: (_d, row) => {
      setNotice({ severity: 'success', text: `${row.appointmentId} marked completed.` });
      refresh();
    },
    onError: (err) =>
      setNotice({ severity: 'error', text: errorText(err, "Couldn't complete the appointment") }),
  });

  const doExport = async () => {
    setExporting(true);
    try {
      downloadBlob(
        await exportCompletedConsultations(filters),
        `completed-consultations-${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      setNotice({ severity: 'error', text: errorText(err, "Couldn't export the list") });
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<GridColDef<CompletedConsultationRow>[]>(
    () => [
      { field: 'appointmentId', headerName: 'Appointment ID', width: 120 },
      {
        field: 'lead',
        headerName: 'Student / Lead',
        minWidth: 170,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Link
            component={RouterLink}
            to={`/leads/${row.lead.id}`}
            state={leadDetailState(location)}
            underline="hover"
            noWrap
          >
            {row.lead.name}
          </Link>
        ),
      },
      {
        field: 'phone',
        headerName: 'Contact Number',
        width: 135,
        sortable: false,
        valueGetter: (_v, row) => row.lead.phone,
      },
      {
        field: 'email',
        headerName: 'Email',
        minWidth: 180,
        flex: 1,
        sortable: false,
        valueGetter: (_v, row) => row.lead.email ?? '—',
      },
      {
        field: 'appointmentDate',
        headerName: 'Appointment Date',
        width: 125,
        sortable: false,
        valueFormatter: (v: string | null) => date(v),
      },
      {
        field: 'appointmentTime',
        headerName: 'Time',
        width: 75,
        sortable: false,
        valueGetter: (_v, row) => time(row.appointmentDate),
      },
      {
        field: 'assignedCounselor',
        headerName: 'Assigned Counselor',
        minWidth: 160,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.assignedCounselor ? (
            <Tooltip
              title={
                row.otherCounselors.length
                  ? `Also assigned: ${row.otherCounselors.map((c) => c.name).join(', ')}`
                  : ''
              }
            >
              <Typography variant="body2" noWrap>
                {row.assignedCounselor.name}
                {row.otherCounselors.length ? ` +${row.otherCounselors.length}` : ''}
              </Typography>
            </Tooltip>
          ) : (
            '—'
          ),
      },
      {
        field: 'counselorLogin',
        headerName: 'Counselor Login',
        minWidth: 180,
        flex: 1,
        sortable: false,
        valueGetter: (_v, row) => row.assignedCounselor?.login ?? '—',
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={STATUS_LABEL[row.status]}
            color={STATUS_COLOR[row.status]}
            variant={row.status === 'SCHEDULED' ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        field: 'completedAt',
        headerName: 'Completed Date',
        width: 150,
        sortable: false,
        valueFormatter: (v: string | null) => dateTime(v),
      },
      {
        field: 'completedBy',
        headerName: 'Completed By',
        minWidth: 170,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) =>
          row.completedBy ? (
            <PersonCell name={row.completedBy.name} login={row.completedBy.login} />
          ) : (
            '—'
          ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Tooltip title="Status history">
              <IconButton
                size="small"
                aria-label={`History of ${row.appointmentId}`}
                onClick={() => setHistoryTarget(row)}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canChangeStatus && row.status === 'COMPLETED' && (
              <Tooltip
                title={
                  row.canReopen
                    ? 'Reopen — completed by mistake'
                    : 'Already registered as a student — can’t be reopened'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Reopen ${row.appointmentId}`}
                    disabled={!row.canReopen}
                    onClick={() => {
                      reopen.reset();
                      setReason('');
                      setReopenTarget(row);
                    }}
                  >
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canChangeStatus && row.status === 'SCHEDULED' && (
              <Tooltip title="Mark completed">
                <IconButton
                  size="small"
                  color="success"
                  aria-label={`Complete ${row.appointmentId}`}
                  disabled={complete.isPending}
                  onClick={() => complete.mutate(row)}
                >
                  <TaskAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutation objects are stable enough; `complete.isPending` re-renders the grid
    [canChangeStatus, complete.isPending],
  );

  const s = summary.data;
  const branches = options.data?.branches ?? [];
  const statusIsCompleted = (filters.status ?? 'COMPLETED') === 'COMPLETED';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <PageHeader
        icon={EventAvailableOutlinedIcon}
        color="#12B76A"
        title="Completed Consultation"
        subtitle="Personal Appointments marked completed — with the counselor who handled each one and their login."
        actions={
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => void doExport()}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        }
      />

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          mb: 2,
          alignItems: 'start',
          gridTemplateColumns: { xs: '1fr', md: '3fr 1fr' },
        }}
      >
        <KpiGrid>
          <KpiCard
            compact
            label={
              statusIsCompleted
                ? 'Total completed consultations'
                : `${STATUS_LABEL[filters.status!]} (matching filters)`
            }
            value={s?.total ?? '—'}
            icon={EventAvailableOutlinedIcon}
            color="#12B76A"
          />
          <KpiCard
            compact
            label="Completed today"
            value={s?.completedToday ?? '—'}
            icon={TodayOutlinedIcon}
            color="#2F6FED"
          />
          <KpiCard
            compact
            label="Completed this month"
            value={s?.completedThisMonth ?? '—'}
            icon={CalendarMonthOutlinedIcon}
            color="#7C3AED"
          />
        </KpiGrid>
        {/* The same height as the tiles beside it; longer lists scroll. */}
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, height: { md: 108 }, maxHeight: 220, overflow: 'auto' }}
          data-testid="by-counselor"
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Consultations by counselor
          </Typography>
          {s && s.byCounselor.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              None
            </Typography>
          )}
          {s?.byCounselor.map((c) => (
            <Stack
              key={c.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              sx={{ mt: 0.75 }}
            >
              <PersonCell name={c.name} login={c.login} />
              <Typography variant="subtitle2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {c.count}
              </Typography>
            </Stack>
          ))}
        </Paper>
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            },
          }}
        >
          <TextField
            size="small"
            label="Search"
            placeholder="Name, phone, email, appointment ID, counselor or login"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ gridColumn: { md: 'span 2' } }}
          />
          <TextField
            select
            size="small"
            label="Counselor"
            value={filters.counselorId ?? ''}
            onChange={(e) =>
              setFilter('counselorId', e.target.value === '' ? '' : Number(e.target.value))
            }
          >
            <MenuItem value="">All counselors</MenuItem>
            {(options.data?.counselors ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name} — {c.login}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Status"
            value={filters.status ?? 'COMPLETED'}
            onChange={(e) =>
              setFilter(
                'status',
                e.target.value === 'COMPLETED' ? '' : (e.target.value as PersonalAppointmentStatus),
              )
            }
          >
            <MenuItem value="COMPLETED">Completed</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="CANCELLED">Cancelled</MenuItem>
          </TextField>
          {branches.length > 1 && (
            <TextField
              select
              size="small"
              label="Branch"
              value={filters.branchId ?? ''}
              onChange={(e) =>
                setFilter('branchId', e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              <MenuItem value="">All branches</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {(
            [
              ['appointmentFrom', 'Appointment from'],
              ['appointmentTo', 'Appointment to'],
              ['completedFrom', 'Completed from'],
              ['completedTo', 'Completed to'],
            ] as const
          ).map(([key, label]) => (
            <TextField
              key={key}
              size="small"
              type="date"
              label={label}
              InputLabelProps={{ shrink: true }}
              value={filters[key] ?? ''}
              onChange={(e) => setFilter(key, e.target.value)}
            />
          ))}
          <Button
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!hasFilters}
            sx={{ justifySelf: 'start', alignSelf: 'center' }}
          >
            Clear filters
          </Button>
        </Box>
      </Paper>

      {list.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorText(list.error, "Couldn't load the consultations")}
        </Alert>
      )}

      <Box sx={{ height: 620, bgcolor: 'background.paper', borderRadius: 1 }}>
        <DataGrid
          rows={list.data?.data ?? []}
          columns={columns}
          rowCount={list.data?.meta.total ?? 0}
          loading={list.isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          localeText={{
            noRowsLabel: hasFilters
              ? 'No consultations match these filters.'
              : 'No completed consultations yet.',
          }}
          sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 0.5 } }}
        />
      </Box>

      <Dialog
        open={!!reopenTarget}
        onClose={reopen.isPending ? undefined : () => setReopenTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reopen {reopenTarget?.appointmentId}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {reopenTarget?.lead.name}&apos;s appointment goes back to <strong>Scheduled</strong> and
            leaves this list. Who completed it, and when, stays in its history.
          </Typography>
          <TextField
            label="Reason"
            helperText="Required — kept in the appointment's history."
            fullWidth
            multiline
            minRows={2}
            autoFocus
            inputProps={{ maxLength: 500 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {reopen.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorText(reopen.error, "Couldn't reopen the appointment")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenTarget(null)} disabled={reopen.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reason.trim() || reopen.isPending}
            onClick={() => reopen.mutate()}
          >
            {reopen.isPending ? 'Reopening…' : 'Reopen appointment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historyTarget} onClose={() => setHistoryTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Status history — {historyTarget?.appointmentId} · {historyTarget?.lead.name}
        </DialogTitle>
        <DialogContent dividers>
          {history.isLoading && <CircularProgress size={24} />}
          {history.data && (
            <Table size="small" aria-label="Status history">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.data.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(h.at)}</TableCell>
                    <TableCell>
                      {h.action === 'CREATED'
                        ? 'Created'
                        : `${h.from ? STATUS_LABEL[h.from] : '—'} → ${h.to ? STATUS_LABEL[h.to] : '—'}`}
                      {h.reason && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          Reason: {h.reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {h.by ? <PersonCell name={h.by.name} login={h.by.login} /> : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
